import { httpClient } from '../http-client';
import { buildQueryString } from './query-builder';
import { ApiResponse } from '../../types/responses/api-response';

// ===== MANAGER API TYPES =====

export interface PlatformMetrics {
  totalUsers: number;
  activeUsers: number;
  totalEvents: number;
  activeEvents: number;
  totalContent: number;
  pendingModeration: number;
  totalRevenue: number;
  avgUserEngagement: number;
  performanceScore: number;
}

export interface ContentItem {
  id: string;
  type: 'event' | 'comment' | 'review' | 'post';
  title: string;
  content: string;
  authorId: string;
  authorName: string;
  status: 'pending' | 'approved' | 'rejected' | 'flagged';
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
  reportCount: number;
}

export interface UserAnalytics {
  totalUsers: number;
  activeUsers: number;
  newUsers: number;
  registrationTrend: Array<{ month: string; count: number }>;
  activityTrend: Array<{ month: string; active: number }>;
  topCategories: Array<{ category: string; userCount: number }>;
}

export interface UserEngagement {
  avgSessionDuration: number;
  avgEventsPerUser: number;
  avgLoginFrequency: number;
  engagementScore: number;
  engagementByRole: Array<{ role: string; score: number }>;
}

export interface UserRetention {
  retentionRate: number;
  churnRate: number;
  cohortAnalysis: Array<{
    cohort: string;
    retention: number;
  }>;
}

export interface NotificationStats {
  totalSent: number;
  delivered: number;
  opened: number;
  clicked: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
}

export interface ManagerNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  recipientCount: number;
  sentAt: string;
  status: string;
}

// Query параметры
export interface ContentQuery {
  page?: number;
  limit?: number;
  type?: 'event' | 'comment' | 'review' | 'post';
  status?: 'pending' | 'approved' | 'rejected' | 'flagged';
  priority?: 'low' | 'medium' | 'high';
  authorId?: string;
}

export interface ContentActionData {
  action: 'reject' | 'flag' | 'archive' | 'feature';
  reason?: string;
}

export interface BulkContentActionData {
  contentIds: string[];
  action: 'reject' | 'flag' | 'archive' | 'delete';
  reason?: string;
}

export interface UserAnalyticsQuery {
  period?: 'week' | 'month' | 'year';
  roleFilter?: string;
}

export interface CreateNotificationData {
  title: string;
  message: string;
  type: 'email' | 'push' | 'sms';
  recipientFilter?: {
    roles?: string[];
    active?: boolean;
    minEvents?: number;
  };
  sendToAll?: boolean;
}

// ===== MANAGER API METHODS =====

export const managerApi = {
  // ==================== МЕТРИКИ ПЛАТФОРМЫ ====================

  /**
   * Получить метрики платформы
   */
  getPlatformMetrics: async (query?: { period?: 'day' | 'week' | 'month' }): Promise<ApiResponse<PlatformMetrics>> => {
    return httpClient.get<PlatformMetrics>(`/manager/platform/metrics${buildQueryString(query || {})}`);
  },

  // ==================== УПРАВЛЕНИЕ КОНТЕНТОМ ====================

  /**
   * Получить контент для модерации
   */
  getContent: async (query?: ContentQuery): Promise<ApiResponse<{
    content: ContentItem[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/manager/content${buildQueryString(query || {})}`);
  },

  /**
   * Выполнить действие с контентом
   */
  performContentAction: async (contentId: string, data: ContentActionData): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.put(`/manager/content/${contentId}/action`, data);
  },

  /**
   * Выполнить массовое действие с контентом
   */
  performBulkContentAction: async (data: BulkContentActionData): Promise<ApiResponse<{
    success: boolean;
    message: string;
    processedCount: number;
  }>> => {
    return httpClient.post('/manager/content/bulk-action', data);
  },

  // ==================== АНАЛИТИКА ПОЛЬЗОВАТЕЛЕЙ ====================

  /**
   * Получить аналитику пользователей
   */
  getUserAnalytics: async (query?: UserAnalyticsQuery): Promise<ApiResponse<UserAnalytics>> => {
    return httpClient.get<UserAnalytics>(`/manager/analytics/users${buildQueryString(query || {})}`);
  },

  /**
   * Получить вовлеченность пользователей
   */
  getUserEngagement: async (period?: 'week' | 'month' | 'year'): Promise<ApiResponse<UserEngagement>> => {
    return httpClient.get<UserEngagement>(`/manager/analytics/users/engagement${buildQueryString({ period })}`);
  },

  /**
   * Получить удержание пользователей
   */
  getUserRetention: async (period?: 'week' | 'month' | 'year'): Promise<ApiResponse<UserRetention>> => {
    return httpClient.get<UserRetention>(`/manager/analytics/users/retention${buildQueryString({ period })}`);
  },

  // ==================== УВЕДОМЛЕНИЯ ====================

  /**
   * Получить уведомления
   */
  getNotifications: async (query?: { page?: number; limit?: number }): Promise<ApiResponse<{
    notifications: ManagerNotification[];
    total: number;
  }>> => {
    return httpClient.get(`/manager/notifications${buildQueryString(query || {})}`);
  },

  /**
   * Создать уведомление
   */
  createNotification: async (data: CreateNotificationData): Promise<ApiResponse<{
    success: boolean;
    message: string;
    notificationId: string;
  }>> => {
    return httpClient.post('/manager/notifications', data);
  },

  /**
   * Получить статистику уведомлений
   */
  getNotificationStats: async (): Promise<ApiResponse<NotificationStats>> => {
    return httpClient.get<NotificationStats>('/manager/notifications/stats');
  },

  // ==================== МОБИЛЬНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить мобильную панель менеджера
   */
  getMobileDashboard: async (): Promise<ApiResponse<{
    metrics: PlatformMetrics;
    recentContent: ContentItem[];
    pendingTasks: number;
  }>> => {
    return httpClient.get('/manager/mobile/dashboard');
  },

  /**
   * Получить быстрые метрики
   */
  getQuickMetrics: async (): Promise<ApiResponse<{
    totalUsers: number;
    activeUsers: number;
    pendingModeration: number;
    systemHealth: string;
  }>> => {
    return httpClient.get('/manager/mobile/quick-metrics');
  },
};

export default managerApi;

